import { getOfflineDb } from '../offline/dbFactory'
import { ensureOfflineReady } from '../offline/initOffline'
import { enqueueEntityChange } from '../offline/outbox/entityOutbox'
import { drainEntityOutboxNow } from '../offline/outbox/syncEntityOutboxNow'
import { dexieFarmRepo } from '../offline/repos/DexieFarmRepo'
import {
  type EntityBatchSyncBlocker,
  type EntityBatchSyncRetryReport,
  type EntityBatchSyncStatus,
  type EntityMutationResult,
  type EntityMutationSyncState,
  readEntityBatchSyncStatus,
  retryEntityBatchSyncStatusWithReport
} from './entityMutationSync'
import { buildProductionUnitMetadata, readCurrentFarm, writeCurrentFarm } from './farmWizard'

export type ProductionUnitRecord = {
  id: string
  name: string
  type: string
  capacity?: number | null
  capacityUnit?: string
}

export type ProductionUnitWizardMutationResult = EntityMutationResult<ProductionUnitRecord[]>

export type ProductionUnitWizardSyncTarget = {
  farmId: string
  productionUnitIds: string[]
}

const isOnline = (): boolean => typeof navigator !== 'undefined' && navigator.onLine

const summarizeOutboxMessages = (messages: Array<string | null>): string | null => {
  const unique = Array.from(
    new Set(
      messages
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
    )
  )

  if (unique.length === 0) return null
  if (unique.length === 1) return unique[0]
  return `${unique[0]} (${unique.length - 1} more issue${unique.length === 2 ? '' : 's'})`
}

const mergeBlockers = (blockers: EntityBatchSyncBlocker[]): EntityBatchSyncBlocker[] => {
  const blockersByKey = new Map<string, EntityBatchSyncBlocker>()

  blockers.forEach((blocker) => {
    const key = `${blocker.entityType}:${blocker.entityId}:${blocker.scope}`
    if (!blockersByKey.has(key)) blockersByKey.set(key, blocker)
  })

  return Array.from(blockersByKey.values())
}

const mergeBatchStatuses = (statuses: EntityBatchSyncStatus[]): EntityBatchSyncStatus => {
  const meaningfulStatuses = statuses.filter((status) => status.syncState !== 'synced' || status.blockedCount > 0)
  if (meaningfulStatuses.length === 0) {
    return {
      syncState: 'synced',
      blockedCount: 0,
      errorMessage: null,
      blockers: []
    }
  }

  const blockers = mergeBlockers(meaningfulStatuses.flatMap((status) => status.blockers))
  const blockedEntityIds = new Set(blockers.map((blocker) => `${blocker.entityType}:${blocker.entityId}`))

  return {
    syncState: meaningfulStatuses.some((status) => status.syncState === 'offline') ? 'offline' : 'pending',
    blockedCount: blockedEntityIds.size,
    errorMessage: summarizeOutboxMessages(meaningfulStatuses.map((status) => status.errorMessage)),
    blockers
  }
}

const normalizeSyncTarget = (target: ProductionUnitWizardSyncTarget): ProductionUnitWizardSyncTarget => ({
  farmId: String(target.farmId ?? '').trim(),
  productionUnitIds: Array.from(
    new Set(target.productionUnitIds.map((id) => String(id ?? '').trim()).filter(Boolean))
  )
})

export const readProductionUnitWizardSyncStatus = async (
  target: ProductionUnitWizardSyncTarget
): Promise<EntityBatchSyncStatus> => {
  const normalizedTarget = normalizeSyncTarget(target)
  if (!normalizedTarget.farmId && normalizedTarget.productionUnitIds.length === 0) {
    return {
      syncState: 'synced',
      blockedCount: 0,
      errorMessage: null,
      blockers: []
    }
  }

  const [farmStatus, productionUnitStatus] = await Promise.all([
    normalizedTarget.farmId
      ? readEntityBatchSyncStatus('farm', [normalizedTarget.farmId])
      : Promise.resolve({ syncState: 'synced', blockedCount: 0, errorMessage: null, blockers: [] } as EntityBatchSyncStatus),
    normalizedTarget.productionUnitIds.length > 0
      ? readEntityBatchSyncStatus('productionUnit', normalizedTarget.productionUnitIds)
      : Promise.resolve({ syncState: 'synced', blockedCount: 0, errorMessage: null, blockers: [] } as EntityBatchSyncStatus)
  ])

  return mergeBatchStatuses([farmStatus, productionUnitStatus])
}

export const retryProductionUnitWizardSyncStatusWithReport = async (
  target: ProductionUnitWizardSyncTarget,
  options?: { maxEntries?: number; maxPasses?: number }
): Promise<EntityBatchSyncRetryReport> => {
  const normalizedTarget = normalizeSyncTarget(target)
  if (!normalizedTarget.farmId && normalizedTarget.productionUnitIds.length === 0) {
    return {
      status: {
        syncState: 'synced',
        blockedCount: 0,
        errorMessage: null,
        blockers: []
      },
      skippedNotFoundCount: 0
    }
  }

  const reports = await Promise.all([
    normalizedTarget.farmId
      ? retryEntityBatchSyncStatusWithReport({
        entityType: 'farm',
        entityIds: [normalizedTarget.farmId],
        maxEntries: options?.maxEntries,
        maxPasses: options?.maxPasses
      })
      : Promise.resolve({
        status: {
          syncState: 'synced',
          blockedCount: 0,
          errorMessage: null,
          blockers: []
        },
        skippedNotFoundCount: 0
      } as EntityBatchSyncRetryReport),
    normalizedTarget.productionUnitIds.length > 0
      ? retryEntityBatchSyncStatusWithReport({
        entityType: 'productionUnit',
        entityIds: normalizedTarget.productionUnitIds,
        maxEntries: options?.maxEntries,
        maxPasses: options?.maxPasses
      })
      : Promise.resolve({
        status: {
          syncState: 'synced',
          blockedCount: 0,
          errorMessage: null,
          blockers: []
        },
        skippedNotFoundCount: 0
      } as EntityBatchSyncRetryReport)
  ])

  return {
    status: mergeBatchStatuses(reports.map((report) => report.status)),
    skippedNotFoundCount: reports.reduce((total, report) => total + report.skippedNotFoundCount, 0)
  }
}

const readProductionUnitWizardSyncState = async (
  target: ProductionUnitWizardSyncTarget
): Promise<EntityMutationSyncState> => {
  const trackedProductionUnitIds = new Set(
    target.productionUnitIds
      .map((id) => String(id ?? '').trim())
      .filter(Boolean)
  )

  const entries = await getOfflineDb().entityOutbox.toArray()
  const hasPending = entries.some((entry) => {
    if (entry.entityType === 'farm') {
      return entry.entityId === target.farmId
    }

    if (entry.entityType !== 'productionUnit') return false
    return trackedProductionUnitIds.has(entry.entityId)
  })

  return hasPending ? 'pending' : 'synced'
}

const buildProductionUnitWizardMutationResult = (
  data: ProductionUnitRecord[],
  target: ProductionUnitWizardSyncTarget
): ProductionUnitWizardMutationResult => {
  if (!isOnline()) {
    return {
      data,
      syncState: 'offline',
      syncPromise: null
    }
  }

  const syncPromise = (async (): Promise<EntityMutationSyncState> => {
    try {
      await drainEntityOutboxNow({ maxEntries: 25, maxPasses: 5 })
    } catch (error) {
      console.warn('[productionUnitWizardApi] failed to drain entity outbox', error)
    }

    return readProductionUnitWizardSyncState(target)
  })()

  return {
    data,
    syncState: 'pending',
    syncPromise
  }
}

export const saveProductionUnits = async (
  nextUnits: ProductionUnitRecord[]
): Promise<ProductionUnitWizardMutationResult> => {
  await ensureOfflineReady()

  const current = (readCurrentFarm() ?? {}) as any
  const farmId = typeof current?.id === 'string' ? current.id.trim() : ''
  if (!farmId) {
    throw new Error('Facility is required before saving production units')
  }

  const base = (await dexieFarmRepo.getById(farmId)) as any
  const nextFarm: any = { ...base, ...current, productionUnits: nextUnits }

  await dexieFarmRepo.upsert(nextFarm)

  await enqueueEntityChange({
    entityType: 'farm',
    op: 'upsert',
    entityId: nextFarm.id,
    payload: {
      id: nextFarm.id,
      name: nextFarm.name,
      altitude: nextFarm.altitude,
      location: nextFarm.location,
      metadata: nextFarm.metadata
    }
  })

  let prevUnits: ProductionUnitRecord[] = []
  if (Array.isArray(base?.productionUnits)) {
    prevUnits = base.productionUnits
  } else if (Array.isArray(current?.productionUnits)) {
    prevUnits = current.productionUnits
  }

  const prevIds = new Set(prevUnits.map((unit) => unit.id))
  const trackedProductionUnitIds = new Set(nextUnits.map((unit) => unit.id))

  for (const prevId of prevIds) {
    trackedProductionUnitIds.add(prevId)

    if (!nextUnits.some((unit) => unit.id === prevId)) {
      await enqueueEntityChange({
        entityType: 'productionUnit',
        op: 'delete',
        entityId: prevId
      })
    }
  }

  for (const unit of nextUnits) {
    await enqueueEntityChange({
      entityType: 'productionUnit',
      op: 'upsert',
      entityId: unit.id,
      payload: {
        id: unit.id,
        farmId: nextFarm.id,
        name: unit.name,
        altitude: undefined,
        metadata: buildProductionUnitMetadata(null, unit)
      }
    })
  }

  writeCurrentFarm(nextFarm)

  return buildProductionUnitWizardMutationResult(nextUnits, {
    farmId: nextFarm.id,
    productionUnitIds: Array.from(trackedProductionUnitIds)
  })
}